import { AnimatePresence, motion } from "framer-motion";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { formatZoneDuration, formatZoneMultiplier, getZoneColor } from "../../services/zoneUtils";
import { useFavouritesStore, useSpaceStore } from "../../stores";
import type { PlanetData, StarSystem } from "../../types";
import "./Sidebar.css";

export function Sidebar() {
	const selectedObject = useSpaceStore((s) => s.selectedObject);
	const isSidebarOpen = useSpaceStore((s) => s.isSidebarOpen);
	const selectObject = useSpaceStore((s) => s.selectObject);
	const isBottomSheet = useMediaQuery("(max-width: 767px)");

	const toggleFavourite = useFavouritesStore((s) => s.toggleFavourite);
	const favourites = useFavouritesStore((s) => s.favourites);

	function handleClose() {
		selectObject(null);
	}

	const objectId = selectedObject?.data.id ?? null;
	const isFav = objectId ? favourites.has(objectId) : false;

	return (
		<AnimatePresence>
			{isSidebarOpen && selectedObject && (
				<motion.aside
					className="sidebar glass-panel-elevated"
					initial={isBottomSheet ? { y: "100%", opacity: 0 } : { x: "100%", opacity: 0 }}
					animate={isBottomSheet ? { y: 0, opacity: 1 } : { x: 0, opacity: 1 }}
					exit={isBottomSheet ? { y: "100%", opacity: 0 } : { x: "100%", opacity: 0 }}
					transition={{ type: "spring", damping: 25, stiffness: 300 }}
					drag={isBottomSheet ? "y" : false}
					dragConstraints={{ top: 0 }}
					dragElastic={0.2}
					onDragEnd={(_, info) => {
						if (info.offset.y > 100 || info.velocity.y > 500) {
							handleClose();
						}
					}}
				>
					{isBottomSheet && (
						<div className="sidebar-drag-handle">
							<div className="sidebar-drag-bar" />
						</div>
					)}

					<div className="sidebar-header">
						<div className="sidebar-type-badge">
							{selectedObject.type === "star" ? "★ Star System" : "◐ Planet"}
						</div>
						<div className="sidebar-header-actions">
							<button
								type="button"
								className={`sidebar-favourite-btn${isFav ? " sidebar-favourite-btn--active" : ""}`}
								onClick={() => objectId && toggleFavourite(objectId)}
								aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
							>
								<svg
									width="18"
									height="18"
									viewBox="0 0 24 24"
									fill={isFav ? "currentColor" : "none"}
								>
									<title>{isFav ? "Favourited" : "Favourite"}</title>
									<path
										d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
										stroke="currentColor"
										strokeWidth="1.5"
									/>
								</svg>
							</button>
							<button
								type="button"
								className="sidebar-close"
								onClick={handleClose}
								aria-label="Close sidebar"
							>
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
									<title>Close sidebar</title>
									<path
										d="M15 5L5 15M5 5l10 10"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
									/>
								</svg>
							</button>
						</div>
					</div>

					<div className="sidebar-content">
						{selectedObject.type === "star" ? (
							<StarSystemDetails system={selectedObject.data} />
						) : (
							<PlanetDetails planet={selectedObject.data} system={selectedObject.system} />
						)}
					</div>
				</motion.aside>
			)}
		</AnimatePresence>
	);
}

interface StarSystemDetailsProps {
	system: StarSystem;
}

function StarSystemDetails({ system }: StarSystemDetailsProps) {
	const selectObject = useSpaceStore((s) => s.selectObject);
	const showSubtitle = system.name !== system.star.name;

	return (
		<>
			<h2 className="sidebar-title">{system.star.name}</h2>
			{showSubtitle && <p className="sidebar-subtitle">{system.name}</p>}

			<div className="sidebar-zone-banner" style={{ borderColor: getZoneColor(system.zone) }}>
				<span className="zone-badge" style={{ background: getZoneColor(system.zone) }}>
					Zone {system.zone}
				</span>
				<span className="zone-detail">{formatZoneDuration(system.zone)}</span>
				<span className="zone-detail">{formatZoneMultiplier(system.zone)} rewards</span>
			</div>

			<div className="sidebar-stats">
				<StatCard
					label="Distance"
					value={
						system.star.distanceLightYears === 0
							? "Origin"
							: system.star.distanceLightYears.toLocaleString()
					}
					unit={system.star.distanceLightYears === 0 ? undefined : "ly"}
					accent="cyan"
				/>
				<StatCard label="Spectral" value={system.star.spectralClass} accent="gold" />
				<StatCard
					label="Apparent Mag"
					value={system.star.apparentMagnitude.toFixed(2)}
					accent="purple"
				/>
				<StatCard label="Planets" value={String(system.planets.length)} accent="pink" />
			</div>

			{system.planets.length > 0 && (
				<div className="sidebar-section">
					<h3>Planets</h3>
					<div className="planet-list">
						{system.planets.map((planet) => (
							<button
								key={planet.id}
								type="button"
								className="planet-row"
								onClick={() => selectObject({ type: "planet", data: planet, system })}
							>
								<span className="planet-dot" style={{ background: planet.color }} />
								<span className="planet-name">{planet.name}</span>
								<span className="planet-orbit">{planet.orbitalRadius.toFixed(2)} AU</span>
								<span className="planet-chevron" aria-hidden="true">
									&#8250;
								</span>
							</button>
						))}
					</div>
				</div>
			)}

			<div className="sidebar-section">
				<h3>Coordinates</h3>
				<div className="coord-grid">
					<div className="coord-item">
						<span className="coord-label">X</span>
						<span className="coord-value">{system.x.toFixed(1)} ly</span>
					</div>
					<div className="coord-item">
						<span className="coord-label">Y</span>
						<span className="coord-value">{system.y.toFixed(1)} ly</span>
					</div>
				</div>
			</div>
		</>
	);
}

interface PlanetDetailsProps {
	planet: PlanetData;
	system: StarSystem;
}

function PlanetDetails({ planet, system }: PlanetDetailsProps) {
	const startMission = useSpaceStore((s) => s.startMission);

	return (
		<>
			<h2 className="sidebar-title">{planet.name}</h2>
			<p className="sidebar-subtitle">Host: {system.star.name}</p>

			<div className="sidebar-zone-banner" style={{ borderColor: getZoneColor(system.zone) }}>
				<span className="zone-badge" style={{ background: getZoneColor(system.zone) }}>
					Zone {system.zone}
				</span>
				<span className="zone-detail">{formatZoneDuration(system.zone)}</span>
				<span className="zone-detail">{formatZoneMultiplier(system.zone)} rewards</span>
			</div>

			<div className="sidebar-stats">
				<StatCard
					label="Orbital Radius"
					value={planet.orbitalRadius.toFixed(2)}
					unit="AU"
					accent="cyan"
				/>
				<StatCard
					label="Period"
					value={planet.orbitalPeriod.toFixed(1)}
					unit="days"
					accent="purple"
				/>
				<StatCard label="Radius" value={planet.radius.toFixed(2)} unit="R⊕" accent="gold" />
				<StatCard label="Discovery" value={planet.discoveryMethod ?? "N/A"} accent="pink" />
			</div>

			<div className="sidebar-section">
				<button
					type="button"
					className="sidebar-action-btn"
					onClick={() => startMission(planet, system)}
				>
					Send Mission
				</button>
			</div>
		</>
	);
}

interface StatCardProps {
	label: string;
	value: string;
	unit?: string;
	accent: "cyan" | "purple" | "gold" | "pink";
}

function StatCard({ label, value, unit, accent }: StatCardProps) {
	return (
		<div className={`stat-card stat-card--${accent}`}>
			<span className="stat-label">{label}</span>
			<span className="stat-value">{value}</span>
			{unit && <span className="stat-unit">{unit}</span>}
		</div>
	);
}
